/**
 * Wiring layer that keeps suggest-mode orchestration out of FlowDesigner.tsx.
 *
 * Owns the shared `suggestingRef`, wraps real mutations with staged shims, and
 * creates the suggest-mode controller that submit/discard toolbar controls use.
 */

import React from 'react';
import type { WorkflowEdge, WorkflowNode } from '../../../../types/workflow';
import type { UndoHistoryHandle } from '../hooks/useUndoHistory';
import {
  makeStagedMutations,
  type DesignerMutations,
} from './stagedMutations';
import { useSuggestMode } from './useSuggestMode';

type UseSuggestModeWiringParams = {
  realMutations: DesignerMutations;
};

type UseSuggestModeControllerParams = {
  workflowId: string | null;
  suggestingRef: React.MutableRefObject<boolean>;
  getNodes: () => WorkflowNode[];
  getEdges: () => WorkflowEdge[];
  setNodes: (nodes: WorkflowNode[]) => void;
  setEdges: (edges: WorkflowEdge[]) => void;
  undoHistory: UndoHistoryHandle;
  toast: { success: (msg: string) => void; error: (msg: string) => void };
};

export function useSuggestModeWiring({
  realMutations,
}: UseSuggestModeWiringParams) {
  const suggestingRef = React.useRef(false);

  const stagedMutations = makeStagedMutations(realMutations, suggestingRef);

  return {
    stagedMutations,
    suggestingRef,
  };
}

export function useSuggestModeController({
  workflowId,
  suggestingRef,
  getNodes,
  getEdges,
  setNodes,
  setEdges,
  undoHistory,
  toast,
}: UseSuggestModeControllerParams) {
  const suggestMode = useSuggestMode({
    workflowId,
    suggestingRef,
    getNodes,
    getEdges,
    setNodes,
    setEdges,
    undoHistory,
    toast,
  });

  return {
    suggestMode,
    isSuggesting: suggestMode.isSuggesting,
  };
}
